import {
  BrandingClientSet,
  BrandingClientSetResponse,
  BrandingColors,
  BrandingImages,
  BrandingLinks,
  BrandingSetFileItem,
  BrandingSetTextItem,
  DefaultBrandingSet,
  DefaultConfiguration,
  WindowWithBranding,
} from './types';

export const grids = [
  'dev',
  'qa',
  'qan',
  'stg',
  'au',
  'ca',
  'de',
  'jer',
  'sg',
  'uk',
  'us',
  'usb',
  'za',
] as const;
const reservedBrandingNames = new Set([
  'login',
  'me',
  ...grids.flatMap((grid) => [
    `login-${grid}`,
    `webmail-${grid}`,
    `security-${grid}`,
    `block-${grid}`,
  ]),
]);
export function isReservedUrl(name: string | null | undefined): boolean {
  if (name == null || name === '') return false;
  return reservedBrandingNames.has(name.toLowerCase());
}

// Matches microjetty BrandingTransformer / LFS: LINK_* is the URL, LINK_TEXT_* is the label.
const BRANDING_LINKS_KEY_MAP: Record<
  keyof BrandingLinks,
  { href: string; label: string }
> = Object.freeze({
  knowledgeBase: {
    href: 'MPP_LOGINPAGE_LINK_3',
    label: 'MPP_LOGINPAGE_LINK_TEXT_3',
  },
  home: { href: 'MPP_LOGINPAGE_LINK_1', label: 'MPP_LOGINPAGE_LINK_TEXT_1' },
  loginHelp: {
    href: 'MPP_LOGINPAGE_LINK_2',
    label: 'MPP_LOGINPAGE_LINK_TEXT_2',
  },
  support: {
    href: 'MPP_LOGINPAGE_LINK_4',
    label: 'MPP_LOGINPAGE_LINK_TEXT_4',
  },
});

const BRANDING_COLOR_KEY_MAP: Record<keyof BrandingColors, string> =
  Object.freeze({
    brandColor: 'BRAND_COLOUR',
    headerMenuBackColour: 'MENU_BACK_COLOUR',
    headerMenuTextColour: 'MENU_TEXT_COLOUR',
    highlightColour: 'HIGHLIGHT_COLOUR',
  });

const BRANDING_DISCLAIMER_KEY_MAP = Object.freeze({
  disclaimerTitle: 'MPP_DISCLAIMER_TITLE',
  disclaimerBody: 'MPP_DISCLAIMER_BODY',
});

const ASSET_KEY_MAP: Record<keyof BrandingImages, string> = Object.freeze({
  favIcon: 'MPP_FAVICON_LOGO_ID',
  favIconMobile: 'MOBILEICON_LOGO_ID',
  logo: 'MPP_MAINPAGE_LOGO_ID',
  loginPageLogo: 'MPP_LOGINPAGE_LOGO_ID',
});

const DEFAULT_MIMECAST_HOME_URL = 'https://www.mimecast.com/';
const DEFAULT_MIMECAST_BASE_URL = 'https://mimecast.com';

export function getSubdomain(url: string): string | null {
  try {
    const hostname = new URL(url).hostname;
    const parts = hostname.split('.');
    return parts.length > 2 ? parts[0] : null;
  } catch {
    return null;
  }
}

export function getBrandingSubdomain(url: string): string | null {
  const subdomain = getSubdomain(url);
  if (subdomain === null || isReservedUrl(subdomain)) {
    return null;
  }
  return subdomain;
}

export function getBrandingSetNameFromUrl(url: string): string | null {
  return getBrandingSubdomain(url);
}

export function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function getCustomerTokenFromUrl(url: string): string | null {
  try {
    const searchParams = new URL(url).searchParams;
    return searchParams.get('customerToken') ?? searchParams.get('tkn');
  } catch {
    return null;
  }
}

export function getContextPath(url: string): string | null {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('/')[1] || null;
  } catch {
    return null;
  }
}

export function isBrandingSetFileItem(
  item: BrandingSetTextItem | BrandingSetFileItem | null | undefined
): item is BrandingSetFileItem {
  return !!item && 'link' in item;
}

export function findBrandingSetItemValueByKey(
  items: Array<BrandingSetTextItem | BrandingSetFileItem>,
  key: string
): string | null {
  const value = items.find((item) => item.key === key)?.name;
  return value ?? null;
}

export function findBrandingSetItemLinkByKey(
  items: Array<BrandingSetTextItem | BrandingSetFileItem>,
  key: string
): string | null {
  const item = items.find((item) => item.key === key);
  return isBrandingSetFileItem(item) ? item.link : null;
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function transformBrandingImageUrl(
  item: BrandingSetFileItem
): Promise<string> {
  try {
    const url = new URL(item.link);
    const apiPrefix = url.hostname.split('-')[0];
    const brandingPath = url.pathname
      .replace(/^\/branding\//, '')
      .replace(/^\//, '');
    const version = await sha256Hex(
      `${item.key}|${item.name}|${item.type}|${item.size}|${item.link}`
    );
    return `/branding-assets/${apiPrefix}/${brandingPath}?v=${version}`;
  } catch {
    return item.link;
  }
}

/** LINK_* holds the URL in `name` for text items and in `link` for file items. */
function resolveBrandingLinkHref(
  items: Array<BrandingSetTextItem | BrandingSetFileItem>,
  hrefKey: string
): string | null {
  const named = findBrandingSetItemValueByKey(items, hrefKey)?.trim() ?? '';
  if (named) {
    return named;
  }
  const linked = findBrandingSetItemLinkByKey(items, hrefKey)?.trim() ?? '';
  return linked || null;
}

function linksToDefaultConfiguration(
  links: BrandingLinks
): DefaultConfiguration {
  return {
    knowledgeBase: {
      href: links.knowledgeBase.href,
      label: links.knowledgeBase.label,
    },
    home: { href: links.home.href, label: links.home.label },
    loginHelp: { href: links.loginHelp.href, label: links.loginHelp.label },
    support: { href: links.support.href, label: links.support.label },
  };
}

export function getDefaultBranding(contextPath: string): BrandingClientSet {
  const links: BrandingLinks = {
    home: {
      default: true,
      href: DEFAULT_MIMECAST_HOME_URL,
      label: 'LOGIN_HOME_LNK',
    },
    knowledgeBase: {
      default: true,
      href: DEFAULT_MIMECAST_BASE_URL,
      label: 'LOGIN_KNOWLEDGE_BASE_LNK',
    },
    loginHelp: {
      default: true,
      href: DEFAULT_MIMECAST_BASE_URL,
      label: 'LOGIN_LOGIN_HELP_LNK',
    },
    support: {
      default: true,
      href: DEFAULT_MIMECAST_BASE_URL,
      label: 'LOGIN_LOGIN_SUPPORT_LNK',
    },
  };
  const images = {
    favIcon: `/${contextPath}/resources/images/favicon.ico`,
    favIconMobile: `/${contextPath}/resources/images/favicon-mobile.png`,
    logo: `/${contextPath}/resources/images/mimecast-logo.png`,
    loginPageLogo: `/${contextPath}/resources/images/mimecast-logo.png`,
  };
  const defaultBranding: DefaultBrandingSet = Object.freeze({
    originalContextPath: contextPath,
    branded: false,
    ...links,
    ...images,
  });
  const defaultConfiguration = linksToDefaultConfiguration(links);
  return Object.freeze({
    defaultBranding,
    defaultConfiguration,
    originalContextPath: contextPath,
    branded: false,
    ...links,
    ...images,
  }) as BrandingClientSet;
}

function sanitizeBrandName(value: string): string {
  return value.trim().replace(/<[^>]*>/g, '');
}

/**
 * True when the API items include at least one colour value or file asset link.
 * Derived from response items (not the mutated set) so default logo paths never
 * count as visual branding.
 */
function hasVisualBrandingItems(
  items: Array<BrandingSetTextItem | BrandingSetFileItem>
): boolean {
  const colourKeys = new Set(Object.values(BRANDING_COLOR_KEY_MAP));
  const assetKeys = new Set(Object.values(ASSET_KEY_MAP));

  return items.some((item) => {
    if (colourKeys.has(item.key) && Boolean(item.name?.trim())) {
      return true;
    }
    return (
      assetKeys.has(item.key) &&
      isBrandingSetFileItem(item) &&
      Boolean(item.link?.trim())
    );
  });
}

export async function buildBrandingSetFromResponse(
  contextPath: string,
  response: BrandingClientSetResponse
): Promise<BrandingClientSet> {
  const fullDefault = getDefaultBranding(contextPath);
  const brandingSet: BrandingClientSet = { ...fullDefault };

  const data = response?.data;
  if (!data || !Array.isArray(data) || data.length < 1) {
    return brandingSet;
  }

  const items = data[0].items;
  if (!items?.length) {
    return brandingSet;
  }

  (
    Object.entries(BRANDING_LINKS_KEY_MAP) as [
      keyof BrandingLinks,
      { href: string; label: string },
    ][]
  ).forEach(([key, { href: hrefKey, label: labelKey }]) => {
    const href = resolveBrandingLinkHref(items, hrefKey);
    if (!href) {
      return;
    }
    const label =
      findBrandingSetItemValueByKey(items, labelKey)?.trim() ||
      brandingSet[key].label;
    brandingSet[key] = { default: false, href, label };
  });

  brandingSet.defaultConfiguration = linksToDefaultConfiguration({
    home: brandingSet.home,
    knowledgeBase: brandingSet.knowledgeBase,
    loginHelp: brandingSet.loginHelp,
    support: brandingSet.support,
  });

  (
    Object.entries(BRANDING_COLOR_KEY_MAP) as [keyof BrandingColors, string][]
  ).forEach(([key, apiKey]) => {
    (brandingSet as unknown as Record<string, string>)[key] =
      findBrandingSetItemValueByKey(items, apiKey) ?? '';
  });

  (
    Object.entries(BRANDING_DISCLAIMER_KEY_MAP) as [
      'disclaimerTitle' | 'disclaimerBody',
      string,
    ][]
  ).forEach(([key, apiKey]) => {
    brandingSet[key] = findBrandingSetItemValueByKey(items, apiKey) ?? '';
  });

  const companyDisplayName =
    findBrandingSetItemValueByKey(items, 'COMPANY_DISPLAY_NAME') ?? '';
  const brandName = findBrandingSetItemValueByKey(items, 'BRAND_NAME') ?? '';
  brandingSet.brandName = sanitizeBrandName(companyDisplayName || brandName);

  const assetEntries = await Promise.all(
    (Object.entries(ASSET_KEY_MAP) as [keyof BrandingImages, string][]).map(
      async ([key, value]) => {
        const item = items.find((i) => i.key === value);
        const url =
          item && isBrandingSetFileItem(item)
            ? await transformBrandingImageUrl(item)
            : '';
        return [key, url] as const;
      }
    )
  );
  assetEntries.forEach(([key, url]) => {
    brandingSet[key] = url;
  });

  brandingSet.branded = hasVisualBrandingItems(items);

  return brandingSet;
}

export function updateBrandingCssVars(
  doc: Document,
  branding: BrandingClientSet
): void {
  const root = doc.documentElement;

  if (branding.headerMenuBackColour) {
    root.style.setProperty(
      '--mc-menu-back-color',
      branding.headerMenuBackColour
    );
  }
  if (branding.headerMenuTextColour) {
    root.style.setProperty(
      '--mc-menu-text-color',
      branding.headerMenuTextColour
    );
  }
  if (branding.highlightColour) {
    root.style.setProperty('--mc-highlight-color', branding.highlightColour);
  }
  if (branding.brandColor) {
    root.style.setProperty('--mc-brand-color', branding.brandColor);
  }
}

export function setBrandingOnWindow(
  window: WindowWithBranding,
  branding: BrandingClientSet
): void {
  window.mimecast = window.mimecast ?? {};
  window.mimecast.branding = branding;
  updateBrandingCssVars(window.document, branding);
}

export function getBrandingFromWindow(
  window: typeof globalThis
): BrandingClientSet | null {
  return isWindowWithBranding(window)
    ? (window.mimecast?.branding ?? null)
    : null;
}

export function isWindowWithBranding(
  window: typeof globalThis
): window is WindowWithBranding {
  if (!('mimecast' in window)) {
    return false;
  }
  const mimecast = (window as { mimecast?: unknown }).mimecast;
  return (
    typeof mimecast === 'object' && mimecast !== null && 'branding' in mimecast
  );
}
